// ==================== 1. 核心依赖导入 ====================
import Vue from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'

// 生产环境屏蔽 console.log
if (process.env.NODE_ENV === 'production') {
    console.log = function() {}
    console.info = function() {}
    console.debug = function() {}
    // console.warn 和 console.error 保留，方便排查问题
}

// ==================== 2. UI 框架与样式 ====================
import ElementUI, { Message } from 'element-ui'
import 'element-ui/lib/theme-chalk/index.css'
import './external/iconfonts/iconfont.css'        // 自定义图标库
import '@/assets/variables.less'                   // 全局样式变量
// import { startVersionPolling } from './untils/versionCheck' // 版本检测模块（未启用，暂注释）

// ==================== 3. 自定义指令与工具 ====================
import Directives from './external/comment/directives'
import directivesAll from './directive/index.js'
import { getRouteButtons, mergeButtons } from '@/untils/auth'
// --- 新增：全局资源管理器和清理混入 ---
import globalResource from "@/untils/globalResource";
import clearLargeData from "@/mixins/clearLargeData";

// ==================== 4. 生产环境优化（关闭警告） ====================
if (process.env.NODE_ENV === 'production') {
    Vue.config.warnHandler = () => {}               // 忽略 Vue 运行时警告
    Vue.config.productionTip = false
    // 过滤 ElementUI 的 [Element Warn] 控制台警告
    const originalWarn = console.warn
    console.warn = (...args) => {
        if (args[0] && typeof args[0] === 'string' && args[0].includes('[Element Warn]')) return
        originalWarn.apply(console, args)
    }
}
Vue.config.productionTip = false

// ==================== 5. 插件注册 ====================
Vue.use(Directives)          // 自定义全局指令（external）
Vue.use(directivesAll)       // 自定义全局指令（directive/index）
Vue.use(ElementUI)           // Element UI 组件库

// 修改 ElementUI Dialog 默认行为：允许点击遮罩不关闭（modal 默认 true，改为 false）
ElementUI.Dialog.props.modal.default = false

// ==================== 6. 全局方法挂载 ====================
// 挂载全局资源管理器
Vue.prototype.$globalResource = globalResource;

// 挂载数据清理混入（自动在 beforeDestroy 中清理大数组和 $bus 订阅）
Vue.mixin(clearLargeData);

// 6位随机数字验证码
Vue.prototype.$generateSixDigitCode = () => {
    let code = ''
    for (let i = 0; i < 6; i++) code += Math.floor(Math.random() * 10).toString()
    return code
}

// 安全的 JSON 解析
Vue.prototype.$parseJSON = (jsonString) => {
    try {
        return JSON.parse(jsonString)
    } catch (error) {
        console.error('JSON解析失败:', error)
        return null
    }
}

// 权限按钮相关（来自 auth.js）
Vue.prototype.$getRouteButtons = getRouteButtons
Vue.prototype.$mergeButtons = mergeButtons

// 关闭所有 element-ui 下拉框（用于路由切换时清理）
Vue.prototype.$closeAllSelects = () => {
    document.querySelectorAll('.el-select-dropdown').forEach(el => el.style.display = 'none')
    document.querySelectorAll('.el-select .el-input__inner').forEach(el => el.blur())
}

// ==================== 7. 全局事件总线（增强版，支持自动清理） ====================
Vue.prototype.$bus = new Vue()

// 辅助方法：安全订阅 $bus（自动记录，便于清理）
Vue.prototype.$onBus = function(event, handler) {
    if (!this.__busSubscriptions) {
        this.__busSubscriptions = [];
    }
    this.$bus.$on(event, handler);
    this.__busSubscriptions.push({ event, handler });
};

// 辅助方法：取消所有 $bus 订阅
Vue.prototype.$offBusAll = function() {
    if (this.__busSubscriptions) {
        this.__busSubscriptions.forEach(({ event, handler }) => {
            this.$bus.$off(event, handler);
        });
        this.__busSubscriptions = [];
    }
};

// 获取页面各区域高度（用于动态布局）
Vue.prototype.$getHeights = () => {
    const windowHeight = window.innerHeight || document.documentElement.clientHeight
    const rightContentHeight = document.getElementById('rightCont')?.offsetHeight || 0
    const feetHeight = document.getElementById('feet')?.offsetHeight || 0
    const lastHeight = document.getElementById('last')?.offsetHeight || 40
    return { windowHeight, rightContentHeight, lastHeight, feetHeight }
}

// ==================== 8. 全局混入：授权到期提醒 ====================
let hasShownExpireReminder = false
Vue.mixin({
    mounted() {
        // 仅在首页执行一次
        if (this.$route?.path === '/home' && !hasShownExpireReminder) {
            this.$nextTick(() => this.checkExpireTimeOnce())
        }
        // 为 el-dialog 添加 ESC 键关闭功能
        if (this.$options.name === 'ElDialog') {
            this.$escHandler = (event) => {
                if (event.keyCode === 27 && this.visible && this.closeOnPressEscape !== false) {
                    this.handleClose ? this.handleClose() : this.close()
                }
            }
            document.addEventListener('keydown', this.$escHandler)
            // 注册到全局资源管理器以便清理（因为文档级事件需要移除）
            this.$globalResource.addEventListener(document, 'keydown', this.$escHandler);
        }
    },
    methods: {
        checkExpireTimeOnce() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}')
                const userId = userInfo.id || userInfo.userId
                if (userId === 1) return  // 超级管理员不检查

                const expireTime = userInfo.clinicExipreTime
                if (!expireTime) return

                const today = new Date()
                today.setHours(0, 0, 0, 0)
                const expireDate = new Date(expireTime)
                expireDate.setHours(0, 0, 0, 0)

                const diffDays = Math.ceil((expireDate - today) / (1000 * 60 * 60 * 24))

                // 剩余 1-7 天：黄牌警告
                if (diffDays > 0 && diffDays <= 7) {
                    hasShownExpireReminder = true
                    this.$alert(
                        `<div style="padding: 10px;">
              <p style="font-size: 16px; margin-bottom: 15px;">尊敬的客户，您好！</p>
              <p>您的诊所授权有效期将于 <span style="color: #E6A23C; font-weight: bold; font-size: 18px;">${diffDays}天</span> 后到期（${expireDate.toLocaleDateString()}）。</p>
              <p>为保证系统的正常使用，请您及时联系渠道合作伙伴办理续期事宜。</p>
              <p style="margin-top: 15px; color: #909399;">感谢您的支持与配合！</p>
            </div>`,
                        '授权到期提醒',
                        {
                            dangerouslyUseHTMLString: true,
                            type: 'warning',
                            confirmButtonText: '我知道了',
                            showClose: false,
                            closeOnClickModal: false,
                            closeOnPressEscape: false
                        }
                    ).catch(() => {})
                }

                // 已过期：红牌警告
                if (diffDays <= 0) {
                    hasShownExpireReminder = true
                    this.$alert(
                        `<div style="padding: 10px;">
              <p style="font-size: 16px; margin-bottom: 15px;">尊敬的客户，您好！</p>
              <p>您的诊所授权已过期，请立即联系渠道合作伙伴办理续期事宜。</p>
              <p style="margin-top: 15px; color: #909399;">给您带来的不便，敬请谅解！</p>
            </div>`,
                        '授权已过期',
                        {
                            dangerouslyUseHTMLString: true,
                            type: 'error',
                            confirmButtonText: '我知道了',
                            showClose: false,
                            closeOnClickModal: false,
                            closeOnPressEscape: false
                        }
                    ).catch(() => {})
                }
            } catch (error) {
                console.error('检查授权过期失败:', error)
            }
        }
    },
    beforeDestroy() {
        // 原有的 ESC 清理已经由全局资源管理器管理，但为了保险，仍保留手动移除
        if (this.$escHandler) {
            document.removeEventListener('keydown', this.$escHandler);
            // 同时从全局资源管理器移除（如果注册过）
            // 由于全局资源管理器是数组，无法单独移除，但 clearAll 时会全部移除，所以无妨
        }
    }
})

// ==================== 9. 启动应用 ====================
new Vue({
    router,
    store,
    devTools: true,      // 允许 vue-devtools 调试
    render: h => h(App),
    // 如需启用版本轮询，可取消下面代码的注释，并确保在路由守卫中清除
    /*mounted() {
        const timer = startVersionPolling(30 * 1000, true);
        this.$globalResource.addTimer(timer);
    }*/
}).$mount('#app')